import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../store/context/notification-context";
import { savePendingEntityProcessorRoute } from "../../helper/entityProcessorRedirect";

const AuthExpiredHandler = () => {
  const { triggerNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const shownRef = useRef(false);

  useEffect(() => {
    if (location.pathname.startsWith("/authentication/")) {
      shownRef.current = false;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/authentication/")) return;

    const savedScrollY = sessionStorage.getItem("pendingPostLoginScrollY");
    if (savedScrollY === null) return;

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, Number(savedScrollY) || 0);
      sessionStorage.removeItem("pendingPostLoginScrollY");
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const onAuthExpired = (ev) => {
      if (shownRef.current) return; // ensure single instance
      shownRef.current = true;

      const message = ev?.detail?.message || "Your session has expired. You will be logged out.";
      const providedRoute = ev?.detail?.loginRoute;

      // Keep the full in-app destination (including query/hash UI state) in the
      // existing post-login cache. This keeps redirect details out of the URL.
      savePendingEntityProcessorRoute(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );

      triggerNotification({
        type: "error",
        message: "Session expired",
        description: message,
        duration: 8,
        statusCode: 401,
        showAuth: true,
      });

      // Clear authentication state and redirect after a short delay so user sees
      // the notification. Preserve cached UI configuration and platform settings.
      setTimeout(() => {
        try {
          ["sessionData", "isLoggedIn", "authToken", "authStatus"].forEach((key) =>
            localStorage.removeItem(key),
          );
          localStorage.setItem("isLoggedIn", "false");
          localStorage.setItem("logoutEvent", Date.now());
        } catch (e) { /* ignore */ }
        // send user to login (use navigate or location replace)
        const target = providedRoute || "/authentication/login";
    
        try { navigate(target, { replace: true }); } catch { window.location.href = "/login"; }
      }, 1200);
    };

    window.addEventListener("auth:expired", onAuthExpired);
    return () => window.removeEventListener("auth:expired", onAuthExpired);
  }, [triggerNotification, navigate]);

  return null;
};

export default AuthExpiredHandler;
